import ApiService from './api.service'

/**
 * Issue Photo interface
 */
export interface IssuePhoto {
  id: number
  fileName: string
  url: string
  fileType: string
  fileSize: number
  createdAt: string
  createdBy: number
}

/**
 * Service for managing issue photos
 */
class IssuePhotoService extends ApiService {
  /**
   * Upload photos to an issue
   * @param projectId - The project ID
   * @param issueId - The issue ID
   * @param formData - FormData containing the photos
   * @param onUploadProgress - Optional progress callback
   * @returns Promise with uploaded photos
   */
  async uploadIssuePhotos(
    projectId: string | number,
    issueId: string | number,
    formData: FormData,
    onUploadProgress?: (progressEvent: import('axios').AxiosProgressEvent) => void
  ): Promise<IssuePhoto[] | null> {
    return this.post(
      `/api/project/${projectId}/issues/${issueId}/photos/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress
      }
    )
  }

  /**
   * Get all photos for an issue
   * @param projectId - The project ID
   * @param issueId - The issue ID
   * @returns Promise with list of photos
   */
  async getIssuePhotos(
    projectId: string | number,
    issueId: string | number
  ): Promise<IssuePhoto[] | null> {
    return this.get(`/api/project/${projectId}/issues/${issueId}/photos`)
  }

  /**
   * Delete a photo from an issue
   * @param projectId - The project ID
   * @param issueId - The issue ID
   * @param photoId - The photo ID to delete
   * @returns Promise
   */
  async deleteIssuePhoto(
    projectId: string | number,
    issueId: string | number,
    photoId: string | number
  ): Promise<void | null> {
    return this.delete(`/api/project/${projectId}/issues/${issueId}/photos/${photoId}`)
  }

  /**
   * Get photo URL for display
   * @param projectId - The project ID
   * @param issueId - The issue ID
   * @param photoId - The photo ID
   * @returns The photo URL
   */
  getPhotoUrl(projectId: string | number, issueId: string | number, photoId: string | number): string {
    return `/api/project/${projectId}/issues/${issueId}/photos/${photoId}`
  }
}

export default new IssuePhotoService()
